import { useEffect, useRef } from "react"

import Placeholder from "@tiptap/extension-placeholder"
import { EditorContent, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"

import { Bold, Italic, List, ListOrdered } from "@/components/icons"
import { Toggle } from "@/components/ui/toggle"
import { cn } from "@/lib/utils"

type MiniEditorProps = {
  content: string
  placeholder?: string
  className?: string
  onChange: (html: string) => void
}

/**
 * Lightweight tiptap editor for use-case text fields.
 * Supports bold, italic, bullet list, and ordered list.
 * Stores content as HTML.
 */
export function MiniEditor({ content, placeholder, className, onChange }: MiniEditorProps) {
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // Track whether content is being set programmatically to avoid feedback loops
  const suppressUpdate = useRef(false)

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        code: false,
      }),
      Placeholder.configure({ placeholder: placeholder ?? "Start typing..." }),
    ],
    content: wrapPlainText(content),
    onUpdate: ({ editor: e }) => {
      if (suppressUpdate.current) return
      onChangeRef.current(e.getHTML())
    },
  })

  // Sync external content changes (e.g. loading a different use case)
  useEffect(() => {
    if (!editor) return
    const currentHtml = editor.getHTML()
    const incoming = wrapPlainText(content)
    // Only reset if content actually differs (avoid cursor jump)
    if (normalizeHtml(currentHtml) !== normalizeHtml(incoming)) {
      suppressUpdate.current = true
      editor.commands.setContent(incoming)
      suppressUpdate.current = false
    }
  }, [content, editor])

  if (!editor) return null

  return (
    <div className={cn("mini-editor group rounded-md border", className)}>
      {/* Toolbar - visible on focus-within */}
      <div className="flex items-center gap-0.5 border-b px-1.5 py-1 opacity-60 transition-opacity group-focus-within:opacity-100">
        <Toggle
          aria-label="Bold"
          className="size-7 p-0"
          pressed={editor.isActive("bold")}
          size="sm"
          onPressedChange={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="size-3.5" />
        </Toggle>
        <Toggle
          aria-label="Italic"
          className="size-7 p-0"
          pressed={editor.isActive("italic")}
          size="sm"
          onPressedChange={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="size-3.5" />
        </Toggle>
        <div className="bg-border mx-1 h-4 w-px" />
        <Toggle
          aria-label="Bullet list"
          className="size-7 p-0"
          pressed={editor.isActive("bulletList")}
          size="sm"
          onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="size-3.5" />
        </Toggle>
        <Toggle
          aria-label="Ordered list"
          className="size-7 p-0"
          pressed={editor.isActive("orderedList")}
          size="sm"
          onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="size-3.5" />
        </Toggle>
      </div>

      {/* Editor area */}
      <EditorContent
        className="prose dark:prose-invert prose-sm [&_.tiptap_p.is-editor-empty:first-child::before]:text-muted-foreground max-w-none px-3 py-2 text-sm [&_.tiptap]:min-h-[3.5rem] [&_.tiptap]:outline-none [&_.tiptap_p.is-editor-empty:first-child::before]:pointer-events-none [&_.tiptap_p.is-editor-empty:first-child::before]:float-left [&_.tiptap_p.is-editor-empty:first-child::before]:h-0 [&_.tiptap_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]"
        editor={editor}
      />
    </div>
  )
}

/** Wrap plain text (no HTML tags) in <p> tags for tiptap */
function wrapPlainText(text: string): string {
  if (!text) return ""
  // If it already has HTML tags, return as-is
  if (/<[a-z][\s\S]*>/i.test(text)) return text
  // Otherwise wrap each line in a <p> tag
  return text
    .split("\n")
    .map((line) => `<p>${line || "<br>"}</p>`)
    .join("")
}

/** Normalize HTML for comparison (strip whitespace differences) */
function normalizeHtml(html: string): string {
  return html
    .replace(/<p><\/p>/g, "<p><br></p>")
    .replace(/\s+/g, " ")
    .trim()
}
